'use client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const Schema = z.object({ email: z.string().email() });
type Form = z.infer<typeof Schema>;

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<Form>({ resolver: zodResolver(Schema), defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    // better-auth ≥1.3: forgetPassword was renamed to requestPasswordReset
    const { error } = await authClient.requestPasswordReset({
      email: values.email,
      redirectTo: '/reset-password',
    });
    if (error) {
      setServerError(error.message ?? 'Could not send reset email');
      return;
    }
    setSent(true);
  });

  if (sent) {
    return (
      <Card>
        <h2 className="text-2xl font-bold mb-2">Check your email</h2>
        <p className="text-[var(--color-on-surface-variant)]">
          If an account exists for <strong>{form.getValues('email')}</strong>, we sent a password
          reset link. It expires in 1 hour.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-2xl font-bold mb-6">Reset your password</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          {...form.register('email')}
          error={form.formState.errors.email?.message}
          autoComplete="email"
        />
        {serverError && <p className="text-sm text-[var(--color-error)]">{serverError}</p>}
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Send reset link
        </Button>
      </form>
      <p className="text-sm text-center mt-6">
        <Link href="/login">Back to sign in</Link>
      </p>
    </Card>
  );
}
